// src/components/EditableImageRow.js (NEW FILE)

import React, { useState, useCallback } from 'react';
import TextField from '@mui/material/TextField';
import Checkbox from '@mui/material/Checkbox';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress'; // For saving indicator

// Base URL for images if svg_url is relative
const API_DOMAIN = "https://test-api.stainedglassusa.com";
const API_BASE_URL = "https://test-api.stainedglassusa.com"; // Keep for image URL helper

function EditableImageRow({ image: initialImage, token, onUpdateSuccess }) {
  // State for the editable fields, initialized from props
  const [parentName, setParentName] = useState(initialImage.parent_name || '');
  const [isFeatured, setIsFeatured] = useState(initialImage.is_featured || false);
  // Add state for other fields if you want them editable, e.g.:
  // const [title, setTitle] = useState(initialImage.title || '');

  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');

  // Helper to construct image URL (same as before)
  const getImageUrl = (svgUrl) => {
    if (!svgUrl) return null;
    if (svgUrl.startsWith('http://') || svgUrl.startsWith('https://')) {
      return svgUrl;
    }
    const path = svgUrl.startsWith('/') ? svgUrl : `/${svgUrl}`;
    return `${API_BASE_URL}${path}`;
  };

  const imageUrl = getImageUrl(initialImage.svg_url);

  // useCallback to potentially optimize if passed deeply, but mainly for organization
  const handleSave = useCallback(async () => {
    setIsSaving(true);
    setError('');

    // Prepare only the fields being edited
    const updatedData = {
      image: {
        parent_name: parentName,
        is_featured: isFeatured,
        // Add other fields here if they become editable
        // title: title,
      }
    };

    try {
      const response = await fetch(`${API_DOMAIN}/admin/images/${initialImage.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(updatedData)
      });

      if (!response.ok) {
        // Try to get error details from response body
        let errorMsg = `HTTP error ${response.status}: ${response.statusText}`;
        try {
          const errData = await response.json();
          errorMsg = errData.error || (errData.errors && errData.errors.join(', ')) || errorMsg;
        } catch (e) { /* Ignore if response body isn't JSON */ }
        throw new Error(errorMsg);
      }

      const savedImage = await response.json();
      // Call the success handler passed from the parent component
      // This allows the parent to update its list immediately
      if (onUpdateSuccess) {
        onUpdateSuccess(savedImage);
      }
      // Optionally reset local state if parent updates the prop, or just rely on parent re-render
      // setParentName(savedImage.parent_name || '');
      // setIsFeatured(savedImage.is_featured || false);

    } catch (err) {
      console.error("Save Error:", err);
      setError(err.message);
    } finally {
      setIsSaving(false);
    }
  }, [initialImage.id, parentName, isFeatured, token, onUpdateSuccess]); // Dependencies for useCallback


  return (
    <tr style={{ borderBottom: '1px solid #eee', verticalAlign: 'middle' }}>
      <td style={{ padding: '4px 8px' }}>{initialImage.id}</td>
      <td style={{ padding: '4px 8px' }}>
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={initialImage.title || 'preview'}
            style={{ width: '60px', height: 'auto', border: '1px solid #ccc', display: 'block' }}
          />
        ) : (
          'No URL'
        )}
      </td>
      <td style={{ padding: '4px 8px' }}>
        {/* Make Title editable if needed */}
        {initialImage.title || '---'}
      </td>
      <td style={{ padding: '4px 8px' }}>
        <TextField
          variant="outlined"
          size="small"
          fullWidth
          name="parent_name"
          value={parentName}
          onChange={(e) => setParentName(e.target.value)}
          disabled={isSaving}
          placeholder="Enter Parent Name"
          sx={{ backgroundColor: 'white' }} // Optional: Ensure background on input
        />
      </td>
      <td style={{ textAlign: 'center', padding: '4px 8px' }}>
        <Checkbox
          name="is_featured"
          checked={isFeatured}
          onChange={(e) => setIsFeatured(e.target.checked)}
          disabled={isSaving}
        />
      </td>
      <td style={{ textAlign: 'center', padding: '4px 8px' }}>
        <Button
          variant="contained"
          color="primary"
          size="small"
          onClick={handleSave}
          disabled={isSaving}
          sx={{ minWidth: '60px' }} // Give button some width
        >
          {isSaving ? <CircularProgress size={20} color="inherit" /> : 'Save'}
        </Button>
        {error && <p style={{ color: 'red', fontSize: '0.8em', marginTop: '4px' }}>{error}</p>}
      </td>
    </tr>
  );
}

export default EditableImageRow;